/**
 * The alarm-naming join every map surface makes.
 *
 * Map payloads identify an alarm by id, not by name — the names live in the
 * session's alarm-definition catalog. This hook holds that catalog as an
 * id-keyed lookup and hands back a resolver that turns one site's alarms into
 * chips, so a row, a marker card and the site dashboard all call an alarm the
 * same thing.
 *
 * The alarm-namespace translator comes from the caller because tenant
 * vocabulary is the caller's to decide: a routed map takes it from the signed-in
 * customer, while the marker card serves whichever dashboard mounted it.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectAlarmDefinitions } from '@sophocles/features/reference';
import { toAlarmChips } from '@sophocles/views/map/mapAlarmChips';

/**
 * @param {Function} tAlarm - alarm-namespace translator for the surface's tenant.
 * @returns {(alarms: object[]) => object[]} alarms -> worst-first chip descriptors.
 */
export default function useMapAlarmChips(tAlarm) {
  const { t } = useTranslation();
  const definitions = useSelector(selectAlarmDefinitions);
  const definitionsById = useMemo(
    () => new Map((definitions ?? []).map(definition => [definition.id, definition])),
    [definitions],
  );
  return useCallback(alarms => toAlarmChips(alarms, { definitionsById, tAlarm, t }), [definitionsById, tAlarm, t]);
}
